/**
 * Shared client form schema
 * Used by both the full Client page form and the Quick Client form in the visit wizard
 * so client data is collected consistently regardless of entry point.
 */

import { z } from 'zod'

export const clientFormSchema = z.object({
  firstname: z.string().min(1, 'First name is required').max(50),
  middlename: z.string().max(50).optional().nullable(),
  lastname: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email').max(50).optional().nullable().or(z.literal('')),
  gender: z.enum(['Male', 'Female', 'Others'], {
    required_error: 'Gender is required',
    invalid_type_error: 'Gender is required',
  }),
  birthday: z.string().min(1, 'Date of birth is required'),
  monthly_income: z.number().min(0, 'Income must be positive').default(0),
  civil_status: z.string().min(1, 'Civil status is required').max(30),
  religion: z.string().max(100).optional().nullable(),
  religion_other: z.string().max(100).optional().nullable(), // For "Others" option
  citizenship: z.string().min(1, 'Citizenship is required').max(50).default('Filipino'),
  educational_attainment: z.string().min(1, 'Educational attainment is required').max(50),
  indigenous_group: z.string().max(50).optional().nullable(),
  indigenous_group_other: z.string().max(50).optional().nullable(), // For "Others" option
  street_name: z.string().max(50).optional().nullable(),
  barangay: z.string().max(50).optional().nullable(),
  city: z.string().min(1, 'City is required').max(50),
  province: z.string().min(1, 'Province is required').max(50),
  postal_code: z.string().length(4, 'Postal code must be 4 digits').regex(/^\d+$/, 'Must be numeric').optional().nullable().or(z.literal('')),
  contact_number: z.string().optional().nullable().or(z.literal('')),
})

export type ClientFormData = z.infer<typeof clientFormSchema>

export const clientFormDefaultValues: ClientFormData = {
  firstname: '',
  middlename: '',
  lastname: '',
  email: '',
  gender: undefined as unknown as ClientFormData['gender'],
  birthday: '',
  monthly_income: 0,
  civil_status: '',
  religion: '',
  religion_other: '',
  citizenship: 'Filipino',
  educational_attainment: '',
  indigenous_group: '',
  indigenous_group_other: '',
  street_name: '',
  barangay: '',
  city: '',
  province: '',
  postal_code: '',
  contact_number: '',
}

/**
 * Resolves "Others" selections into their free-text values and converts
 * empty strings to null before sending data to the API.
 */
export function processClientFormData(data: ClientFormData): Record<string, unknown> {
  const processed = { ...data }

  if (data.religion === 'Others' && data.religion_other) {
    processed.religion = data.religion_other
  }

  if (data.indigenous_group === 'Others' && data.indigenous_group_other) {
    processed.indigenous_group = data.indigenous_group_other
  }

  delete (processed as Partial<ClientFormData>).religion_other
  delete (processed as Partial<ClientFormData>).indigenous_group_other

  return Object.fromEntries(
    Object.entries(processed).map(([key, value]) => [key, value === '' ? null : value])
  )
}
